import dayjs from 'dayjs';
import { alert } from '@/services/alert';
import { Avatar, Group } from '@mantine/core';
import { useMedplum } from '@medplum/react';
import { IconCheck } from '@tabler/icons-react';
import { MRT_ColumnDef } from 'mantine-react-table';
import { formatBirthDate } from '@/services/patient';
import { isReferenceOf } from '@bariendo/lib/services';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { dateRangeFilterClauses } from '@/components/Hub/listRange';
import { useCalendarDay, useNow } from '@/hooks/useNow';
import { useEffect, useMemo, useRef, useState } from 'react';
import { formatDate, formatToAdobeSpectrumFormat } from '@/services/date';
import { getPrimaryPhone, getPrimaryEmail } from '@/services/contact-point';
import { Encounter, Practitioner, Reference, Appointment } from '@medplum/fhirtypes';
import { isMedplumRateLimitError, getRateLimitAlert } from '@/utils/medplumRateLimit';
import { getAppointmentHostProvider, extractPractitionersFromAppointment } from '@bariendo/lib/scheduling';
import { formatWeightLb, getWeightLossDisplay, getInitials, getProcedureCity } from '../carrum';
import { getBariatricProcedureStageLabel, getEmployer } from '@/services/patient-fields';
import {
  renderBmiTableCell,
  formatProcedureType,
  isAllPatientListFilter,
  STAGE_TAG_BY_ALL_FILTER,
  appointmentDateSortingFn,
  getLatestBmiValueAndDate,
  getStartingBmiValueAndDate,
  formatPostProcedureTwlDisplay,
} from '../services';
import {
  PatientListPageDataRow,
  PatientListQueryResultItem,
  PractitionerWithPractitionerRoles,
  MyUpcomingAppointmentsQueryResultItem,
  AppointmentWithParticipantAndPractitionerRole,
} from '../types';
import {
  PATIENT_LIST_QUERY,
  CARRUM_PATIENT_LIST_QUERY,
  MY_UPCOMING_APPOINTMENTS_QUERY,
  ALL_CONSULTATION_APPOINTMENTS_QUERY,
  ALL_PRE_PROCEDURE_APPOINTMENTS_QUERY,
  ALL_POST_PROCEDURE_APPOINTMENTS_QUERY,
} from '../graphql';
import { capitalize, isResource, formatHumanName, getReferenceString, formatCodeableConcept } from '@medplum/core';
import {
  Track,
  ProviderRole,
  TRACK_CODESYSTEM,
  BenefitAdministrator,
  BariatricProcedureStage,
  PatientPageFilterOption,
  PATIENT_PAGE_FILTER_OPTIONS,
  BENEFIT_ADMINISTRATOR_CODESYSTEM,
  BARIATRIC_PROCEDURE_STAGE_TAG_SYSTEM,
} from '@bariendo/lib/constants';

/** Tabs that own their query and render outside this hook's shared table. */
const PAC_TABS_WITH_OWN_QUERY: PatientPageFilterOption[] = [
  PATIENT_PAGE_FILTER_OPTIONS.ALL_INQUIRY,
  PATIENT_PAGE_FILTER_OPTIONS.ALL_PRE_CONSULTATION,
  PATIENT_PAGE_FILTER_OPTIONS.ALL_POST_CONSULTATION,
];

export function usePatientList(activeFilter: PatientPageFilterOption, searchTerm: string) {
  const medplum = useMedplum();
  const { currentUser } = useCurrentUser();

  const [data, setData] = useState<PatientListPageDataRow[]>([]);
  const [labReviewEncounterByPatientId, setLabReviewEncounterByPatientId] = useState<Map<string, Encounter>>(new Map());

  const [patientIds, setPatientIds] = useState<string[] | null>(null);
  const [patientIdsLoading, setPatientIdsLoading] = useState(false);
  const patientIdsCacheRef = useRef<Map<string, string[]>>(new Map());

  // My upcoming appointments is a window off today; keyed on the calendar day
  // so it moves at midnight instead of freezing at mount (BAR-2708).
  const today = useCalendarDay(useNow());

  const variables = useMemo(() => {
    // `{}` is what `useLiveGraphQL` short-circuits on. Without this the inquiry
    // tab — which is not a stage-tag filter — would fall through to
    // PATIENT_LIST_QUERY with no variables and page every patient in the project
    // for a table that is not on screen.
    if (PAC_TABS_WITH_OWN_QUERY.includes(activeFilter)) {
      return {};
    }

    const filters: Record<string, string> = {};

    if (
      searchTerm &&
      !isAllPatientListFilter(activeFilter) &&
      activeFilter !== PATIENT_PAGE_FILTER_OPTIONS.MY_UPCOMING_APPOINTMENTS &&
      activeFilter !== PATIENT_PAGE_FILTER_OPTIONS.CARRUM
    ) {
      filters.name = searchTerm;
    }

    const practitionerReference = getReferenceString(currentUser!);

    if (activeFilter === PATIENT_PAGE_FILTER_OPTIONS.MY_PATIENTS && practitionerReference) {
      filters.filter = [
        `_has:CareTeam:subject:participant eq "${practitionerReference}"`,
        `_tag ne "${BARIATRIC_PROCEDURE_STAGE_TAG_SYSTEM}|${BariatricProcedureStage.PRE_CONSULTATION_UNPAID}"`,
      ].join(' and ');
    }

    if (activeFilter === PATIENT_PAGE_FILTER_OPTIONS.MY_UPCOMING_APPOINTMENTS && practitionerReference) {
      filters.practitioner = practitionerReference;
      // Today through three days out, both days whole — see `listRangeBounds`
      // for why the end day is not simply `date le` (BAR-2708).
      const filterParts = [
        ...dateRangeFilterClauses({
          from: dayjs(today).format('YYYY-MM-DD'),
          to: dayjs(today).add(3, 'day').format('YYYY-MM-DD'),
        }),
        `status ne "cancelled" and status ne "entered-in-error"`,
      ];

      if (searchTerm) {
        filterParts.push(`patient.name co "${searchTerm}"`);
      }

      filters.filter = filterParts.join(' and ');
    }

    if (activeFilter === PATIENT_PAGE_FILTER_OPTIONS.CARRUM) {
      // Two vocabularies during the BAR-2602 compatibility window: a legacy
      // episode carries `carrum` as its track, a new one carries `employer`
      // plus a `carrum` benefit-administrator coding. Verified on staging that
      // `or` inside a `_filter` with `_has` returns the deduped union.
      const parts = [
        `(_has:EpisodeOfCare:patient:type eq "${TRACK_CODESYSTEM}|${Track.CARRUM}" or ` +
          `_has:EpisodeOfCare:patient:type eq "${BENEFIT_ADMINISTRATOR_CODESYSTEM}|${BenefitAdministrator.CARRUM}")`,
      ];
      if (searchTerm) {
        parts.push(`name co "${searchTerm}"`);
      }
      filters.filter = parts.join(' and ');
    }

    if (isAllPatientListFilter(activeFilter)) {
      if (patientIds === null || patientIds.length === 0) {
        return {};
      }
      return { patientIds: patientIds.join(',') };
    }

    if (Object.keys(filters).length === 0) {
      return undefined;
    }

    return filters;
  }, [searchTerm, currentUser, activeFilter, patientIds, today]);

  useEffect(() => {
    // Both CI tabs render through their own components, which root their queries
    // on an appointment. Leaving the cohort pre-fetch running would page every
    // tagged Patient for a table that is no longer on screen — and holding
    // `patientIds` at null keeps `variables` at `{}`, which `useLiveGraphQL`
    // short-circuits, so no GraphQL request fires either.
    if (!isAllPatientListFilter(activeFilter) || PAC_TABS_WITH_OWN_QUERY.includes(activeFilter)) {
      setPatientIds(null);
      return;
    }

    const allStageFilter = activeFilter;
    const cacheKey = `${allStageFilter}:${searchTerm}`;
    const cached = patientIdsCacheRef.current.get(cacheKey);
    if (cached) {
      setPatientIds(cached);
      setPatientIdsLoading(false);
      return;
    }

    let cancelled = false;
    setPatientIdsLoading(true);
    setPatientIds(null);

    async function fetchPatientIds() {
      try {
        const allIds: string[] = [];

        for await (const page of medplum.searchResourcePages('Patient', {
          _tag: STAGE_TAG_BY_ALL_FILTER[allStageFilter],
          ...(searchTerm ? { name: searchTerm } : {}),
          _elements: 'id',
          _count: 1000,
        })) {
          if (cancelled) {
            return;
          }
          for (const patient of page) {
            allIds.push(getReferenceString(patient));
          }
        }
        if (!cancelled) {
          patientIdsCacheRef.current.set(cacheKey, allIds);
          setPatientIds(allIds);
        }
      } catch (error: unknown) {
        if (isMedplumRateLimitError(error)) {
          alert.warning({
            ...getRateLimitAlert(
              `The system temporarily limited how fast we can load patients for this list. 
              Wait a few seconds before trying again.`
            ),
          });
          return;
        }

        alert.error({
          title: 'Error loading patients',
          message: 'An error occurred while loading patients. Please try again later.',
        });
      } finally {
        if (!cancelled) {
          setPatientIdsLoading(false);
        }
      }
    }

    fetchPatientIds();
    return () => {
      cancelled = true;
    };
  }, [activeFilter, searchTerm, medplum]);

  useEffect(() => {
    if (isAllPatientListFilter(activeFilter) && (patientIds === null || patientIds.length === 0)) {
      setData([]);
    }
  }, [activeFilter, patientIds]);

  const query = useMemo(() => {
    const filterQueryMap = new Map<PatientPageFilterOption, string>([
      [PATIENT_PAGE_FILTER_OPTIONS.ALL_PRE_PROCEDURE, ALL_PRE_PROCEDURE_APPOINTMENTS_QUERY],
      [PATIENT_PAGE_FILTER_OPTIONS.ALL_POST_PROCEDURE, ALL_POST_PROCEDURE_APPOINTMENTS_QUERY],
      [PATIENT_PAGE_FILTER_OPTIONS.MY_UPCOMING_APPOINTMENTS, MY_UPCOMING_APPOINTMENTS_QUERY],
      [PATIENT_PAGE_FILTER_OPTIONS.ALL_PRE_CONSULTATION, ALL_CONSULTATION_APPOINTMENTS_QUERY],
      [PATIENT_PAGE_FILTER_OPTIONS.ALL_POST_CONSULTATION, ALL_CONSULTATION_APPOINTMENTS_QUERY],
      [PATIENT_PAGE_FILTER_OPTIONS.CARRUM, CARRUM_PATIENT_LIST_QUERY],
    ]);

    return filterQueryMap.get(activeFilter) ?? PATIENT_LIST_QUERY;
  }, [activeFilter]);

  const patientList = useMemo(() => {
    if (activeFilter === PATIENT_PAGE_FILTER_OPTIONS.MY_UPCOMING_APPOINTMENTS) {
      return Array.from(
        (data as unknown as MyUpcomingAppointmentsQueryResultItem[])
          .reduce((patients, appointment) => {
            const patientParticipants = appointment.participant?.filter((participant) =>
              isResource(participant.actor?.resource, 'Patient')
            );

            patientParticipants?.forEach((patientParticipant) => {
              const relatedAppointment = { ...appointment };
              relatedAppointment.participant = [];

              patients.set(patientParticipant.actor.resource.id!, {
                ...patientParticipant.actor.resource,
                relatedAppointment: [relatedAppointment],
              });
            });

            return patients;
          }, new Map<string, PatientListQueryResultItem>())
          .values()
      ).sort(appointmentDateSortingFn);
    }

    if (isAllPatientListFilter(activeFilter)) {
      return Array.from(
        (data as Appointment[])
          .reduce((map, appointment) => {
            const patientParticipant = appointment.participant?.find((p) => isResource(p.actor?.resource, 'Patient'));

            if (!patientParticipant?.actor?.resource) {
              return map;
            }

            const patient = patientParticipant.actor.resource as PatientListQueryResultItem;

            if (!map.has(patient.id!)) {
              map.set(patient.id!, {
                ...patient,
                relatedAppointment: [appointment as unknown as AppointmentWithParticipantAndPractitionerRole],
              });
            }

            return map;
          }, new Map<string, PatientListQueryResultItem>())
          .values()
      );
    }

    return data as PatientListQueryResultItem[];
  }, [data, activeFilter]);

  const initialStateSorting = useMemo(() => {
    if (activeFilter === PATIENT_PAGE_FILTER_OPTIONS.MY_UPCOMING_APPOINTMENTS) {
      return [{ id: 'appointmentTime', desc: false }];
    }

    if (activeFilter === PATIENT_PAGE_FILTER_OPTIONS.MY_PATIENTS) {
      return [{ id: 'lastCommunication', desc: true }];
    }

    if (activeFilter === PATIENT_PAGE_FILTER_OPTIONS.ALL_PRE_CONSULTATION) {
      return [{ id: 'consultationDateTime', desc: false }];
    }

    if (activeFilter === PATIENT_PAGE_FILTER_OPTIONS.ALL_POST_CONSULTATION) {
      return [{ id: 'consultationDateTime', desc: true }];
    }

    if (activeFilter === PATIENT_PAGE_FILTER_OPTIONS.ALL_PRE_PROCEDURE) {
      return [{ id: 'procedureDate', desc: false }];
    }

    if (activeFilter === PATIENT_PAGE_FILTER_OPTIONS.ALL_POST_PROCEDURE) {
      return [{ id: 'procedureDate', desc: true }];
    }

    if (activeFilter === PATIENT_PAGE_FILTER_OPTIONS.CARRUM) {
      return [{ id: 'accountCreated', desc: true }];
    }

    return [];
  }, [activeFilter]);

  const columns = useMemo<MRT_ColumnDef<PatientListQueryResultItem>[]>(() => {
    let additionalColumns: MRT_ColumnDef<PatientListQueryResultItem>[] = [];

    const statusColumn: MRT_ColumnDef<PatientListQueryResultItem> = {
      id: 'status',
      header: 'Status',
      enableSorting: false,
      accessorFn: (patient) => getBariatricProcedureStageLabel(patient) || '--',
    };

    const lastCommunicationColumn: MRT_ColumnDef<PatientListQueryResultItem> = {
      id: 'lastCommunication',
      header: 'Last communication',
      size: 165,
      enableSorting: !isAllPatientListFilter(activeFilter),
      sortingFn: 'datetime',
      accessorFn: (patient) => patient.lastCommunication?.[0]?.sent,
      Cell: ({ row }) => {
        const lastCommunication = row.original.lastCommunication?.[0];
        return formatDate(lastCommunication?.sent);
      },
    };

    const isMyPatientsOrAll =
      activeFilter === PATIENT_PAGE_FILTER_OPTIONS.MY_PATIENTS || activeFilter === PATIENT_PAGE_FILTER_OPTIONS.ALL;

    if (isMyPatientsOrAll) {
      additionalColumns = [
        statusColumn,
        {
          id: 'phoneNumber',
          header: 'Phone number',
          size: 100,
          enableSorting: false,
          accessorFn: (patient) => getPrimaryPhone(patient.telecom)?.value ?? '',
        },
        {
          id: 'email',
          header: 'Email address',
          size: 150,
          enableSorting: false,
          accessorFn: (patient) => getPrimaryEmail(patient.telecom)?.value ?? '',
        },
        lastCommunicationColumn,
      ];
    }

    if (activeFilter === PATIENT_PAGE_FILTER_OPTIONS.MY_UPCOMING_APPOINTMENTS) {
      additionalColumns = [
        statusColumn,
        {
          id: 'appointment',
          header: 'Appointment',
          enableSorting: false,
          accessorFn: (patient) =>
            formatCodeableConcept(patient.relatedAppointment?.[0]?.appointmentType) ?? 'Appointment',
        },
        {
          id: 'appointmentTime',
          header: 'Appointment date & time',
          enableSorting: true,
          accessorFn: (patient) =>
            patient.relatedAppointment?.[0] ? formatToAdobeSpectrumFormat(patient.relatedAppointment?.[0]?.start) : '',
        },
      ];
    }

    if (
      activeFilter === PATIENT_PAGE_FILTER_OPTIONS.ALL_PRE_CONSULTATION ||
      activeFilter === PATIENT_PAGE_FILTER_OPTIONS.ALL_POST_CONSULTATION
    ) {
      additionalColumns = [
        {
          id: 'consultationDateTime',
          header: 'Consultation date & time',
          sortingFn: 'datetime',
          Cell: ({ row }) =>
            row.original.relatedAppointment?.[0] &&
            formatToAdobeSpectrumFormat(row.original.relatedAppointment?.[0]?.start),
          accessorFn: (patient) => patient.relatedAppointment?.[0]?.start,
        },
        {
          id: 'consultationProvider',
          header: 'Consultation NP',
          enableSorting: false,
          accessorFn: (patient) => {
            const participants = patient.relatedAppointment?.[0]?.participant;

            const nurses = participants?.reduce((nurses, participant) => {
              const practitioner = participant.actor?.resource as PractitionerWithPractitionerRoles;

              if (isResource(practitioner, 'Practitioner')) {
                const isNurse = practitioner.practitionerRoles?.[0]?.code?.some(
                  (role) => role.coding?.[0]?.code === ProviderRole.NURSE_PRACTITIONER
                );

                if (isNurse) {
                  nurses.push(formatHumanName(practitioner.name?.[0]));
                }
              }

              return nurses;
            }, [] as string[]);

            return nurses?.join(', ') ?? '';
          },
        },
        lastCommunicationColumn,
      ];
    }

    if (
      activeFilter === PATIENT_PAGE_FILTER_OPTIONS.ALL_PRE_PROCEDURE ||
      activeFilter === PATIENT_PAGE_FILTER_OPTIONS.ALL_POST_PROCEDURE
    ) {
      additionalColumns = [
        {
          id: 'procedureType',
          header: 'Procedure type',
          enableSorting: false,
          size: 270,
          accessorFn: (patient) => formatProcedureType(patient.relatedAppointment?.[0]),
        },
        {
          id: 'procedureDate',
          header: 'Procedure date',
          sortingFn: 'datetime',
          accessorFn: (patient) => patient.relatedAppointment?.[0]?.start,
          Cell: ({ row }) => formatDate(row.original.relatedAppointment?.[0]?.start),
        },
        {
          id: 'physician',
          header: 'Physician',
          enableSorting: false,
          accessorFn: (patient) => {
            const appointment = patient.relatedAppointment?.[0];

            if (!appointment) {
              return '';
            }

            const host = getAppointmentHostProvider(appointment);

            if (host?.actor) {
              return 'resource' in host.actor
                ? formatHumanName((host.actor.resource as Practitioner)?.name?.[0])
                : host?.actor?.display;
            }

            const practitioner = extractPractitionersFromAppointment(appointment)?.[0];
            return isResource(practitioner, 'Practitioner')
              ? formatHumanName((practitioner as Practitioner)?.name?.[0])
              : (practitioner as Reference<Practitioner>)?.display;
          },
        },
        {
          // "Facility", not "Location": this resolves the appointment's Location
          // participant — the building. The CI tabs need "Location" for the
          // market a lead is closest to, which is a different fact, so two
          // meanings can't share the word.
          id: 'facility',
          header: 'Facility',
          enableSorting: false,
          size: 120,
          accessorFn: (patient) =>
            patient.relatedAppointment?.[0]?.participant.find((participant) =>
              isReferenceOf(participant?.actor, 'Location')
            )?.actor?.display ?? '',
        },
        lastCommunicationColumn,
      ];
    }

    if (activeFilter === PATIENT_PAGE_FILTER_OPTIONS.ALL_PRE_PROCEDURE) {
      additionalColumns.push(
        {
          id: 'medicationRequests',
          header: 'Rx orders',
          enableSorting: false,
          size: 100,
          enableColumnActions: false,
          enableColumnFilter: false,
          mantineTableBodyCellProps: () => ({ align: 'center' }),
          mantineTableHeadCellProps: () => ({ align: 'center' }),
          Cell: ({ row }) => !!row.original.medicationRequests?.length && <IconCheck size={16} color="teal" />,
        },
        {
          id: 'labOrders',
          header: 'Lab orders',
          size: 100,
          enableSorting: false,
          mantineTableBodyCellProps: () => ({ align: 'center' }),
          mantineTableHeadCellProps: () => ({ align: 'center' }),
          Cell: ({ row }) => capitalize(row.original.latestLabOrder?.[0]?.status || ''),
        },
        {
          id: 'labsReviewed',
          header: 'Labs reviewed',
          size: 120,
          enableSorting: false,
          mantineTableBodyCellProps: () => ({ align: 'center' }),
          mantineTableHeadCellProps: () => ({ align: 'center' }),
          Cell: ({ row }) =>
            !!labReviewEncounterByPatientId.get(row.original.id!) && <IconCheck size={16} color="teal" />,
        }
      );
    }

    if (activeFilter === PATIENT_PAGE_FILTER_OPTIONS.ALL_POST_PROCEDURE) {
      additionalColumns.push(
        {
          id: 'twl',
          header: 'TWL',
          enableSorting: false,
          accessorFn: (patient) => formatPostProcedureTwlDisplay(patient),
        },
        {
          id: 'startingBMI',
          header: 'Starting BMI',
          size: 110,
          enableSorting: false,
          accessorFn: (patient) => getStartingBmiValueAndDate(patient).value,
          Cell: ({ row }) => {
            const { value, dateIso } = getStartingBmiValueAndDate(row.original);
            return renderBmiTableCell(value, dateIso);
          },
        },
        {
          id: 'latestBMI',
          header: 'Latest BMI',
          size: 100,
          enableSorting: false,
          accessorFn: (patient) => getLatestBmiValueAndDate(patient).value,
          Cell: ({ row }) => {
            const { value, dateIso } = getLatestBmiValueAndDate(row.original);
            return renderBmiTableCell(value, dateIso);
          },
        }
      );
    }

    if (activeFilter === PATIENT_PAGE_FILTER_OPTIONS.CARRUM) {
      additionalColumns = [
        {
          id: 'currentState',
          header: 'Current state',
          enableSorting: false,
          accessorFn: (patient) => getBariatricProcedureStageLabel(patient) || '—',
        },
        {
          id: 'employer',
          header: 'Employer',
          enableSorting: false,
          accessorFn: (patient) => getEmployer(patient) || '—',
        },
        {
          id: 'city',
          header: 'City',
          enableSorting: false,
          accessorFn: (patient) => patient.address?.[0]?.city ?? '—',
        },
        {
          id: 'procedureCity',
          header: 'Procedure city',
          enableSorting: false,
          accessorFn: (patient) => getProcedureCity(patient) || '—',
        },
        {
          id: 'beginningWeight',
          header: 'Beginning weight',
          enableSorting: false,
          accessorFn: (patient) => formatWeightLb(patient.firstWeightObservation?.[0]) || '—',
        },
        {
          id: 'finalWeight',
          header: 'Final weight',
          enableSorting: false,
          accessorFn: (patient) => formatWeightLb(patient.lastWeightObservation?.[0]) || '—',
        },
        {
          id: 'weightLoss',
          header: 'Weight loss',
          enableSorting: false,
          accessorFn: (patient) =>
            getWeightLossDisplay(patient.firstWeightObservation?.[0], patient.lastWeightObservation?.[0]) || '—',
        },
        {
          id: 'accountCreated',
          header: 'Account created',
          sortingFn: 'datetime',
          accessorFn: (patient) => patient.carrumEpisode?.[0]?.period?.start,
          Cell: ({ row }) => formatDate(row.original.carrumEpisode?.[0]?.period?.start) || '—',
        },
        {
          id: 'consult',
          header: 'Consult',
          sortingFn: 'datetime',
          accessorFn: (patient) => patient.consultationAppointment?.[0]?.start,
          Cell: ({ row }) => formatDate(row.original.consultationAppointment?.[0]?.start) || '—',
        },
        {
          id: 'procedure',
          header: 'Procedure',
          sortingFn: 'datetime',
          accessorFn: (patient) => patient.procedureAppointment?.[0]?.start,
          Cell: ({ row }) => formatDate(row.original.procedureAppointment?.[0]?.start) || '—',
        },
      ];
    }

    const nameColumn: MRT_ColumnDef<PatientListQueryResultItem> = {
      id: 'name',
      header: 'Patient',
      enableSorting: isMyPatientsOrAll,
      accessorFn: (patient) => formatHumanName(patient.name?.[0]),
    };

    if (activeFilter === PATIENT_PAGE_FILTER_OPTIONS.CARRUM) {
      nameColumn.Cell = ({ row }) => (
        <Group gap="xs" wrap="nowrap">
          <Avatar radius="xl" size="sm" color="blue">
            {getInitials(row.original.name?.[0])}
          </Avatar>
          <span>{formatHumanName(row.original.name?.[0])}</span>
        </Group>
      );
    }

    const dateOfBirthColumn: MRT_ColumnDef<PatientListQueryResultItem> = {
      id: 'dateOfBirth',
      header: 'Date of birth',
      accessorFn: (patient) => (patient.birthDate ? dayjs(patient.birthDate).valueOf() : Number.MAX_SAFE_INTEGER),
      Cell: ({ row }) => formatBirthDate(row.original.birthDate),
      sortingFn: 'basic',
      enableSorting: isMyPatientsOrAll,
    };

    if (activeFilter === PATIENT_PAGE_FILTER_OPTIONS.CARRUM) {
      return [nameColumn, ...additionalColumns];
    }

    return [nameColumn, dateOfBirthColumn, ...additionalColumns];
  }, [activeFilter, labReviewEncounterByPatientId]);

  useEffect(() => {
    async function getLabReviewEncounterByPatientId() {
      if (activeFilter === PATIENT_PAGE_FILTER_OPTIONS.ALL_PRE_PROCEDURE) {
        patientList.forEach(async (patient) => {
          patient.labReviewTasks?.forEach(async (labReviewTask) => {
            if (labReviewTask.encounter && !isResource(labReviewEncounterByPatientId.get(patient.id!), 'Encounter')) {
              try {
                const encounter = await medplum.readReference<Encounter>(labReviewTask.encounter);
                labReviewEncounterByPatientId.set(patient.id!, encounter);
                setLabReviewEncounterByPatientId(labReviewEncounterByPatientId);
              } catch (_error: any) {
                // continue if encounter is not found
              }
            }
          });
        });
      }
    }

    getLabReviewEncounterByPatientId();
  }, [patientList, medplum, activeFilter]);

  return {
    query,
    columns,
    setData,
    variables,
    patientList,
    initialStateSorting,
    patientIdsLoading,
  };
}
